import { useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { createContext, type ReactNode, useContext, useEffect } from 'react';
import { usePreferenceManagement } from '@/domains/system-management/hooks/usePreferenceManagement';
import type { Preference } from '@/domains/system-management/types/preference';
import Loader from '@/shared/components/ui/Loader/Loader';

export interface PreferenceContextType {
  preferenceData: Preference[] | null;
}

const PreferenceContext = createContext<PreferenceContextType | undefined>(undefined);

interface PreferenceProviderProps {
  children: ReactNode;
}

export const PreferenceProvider: React.FC<PreferenceProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();

  const { usePreference } = usePreferenceManagement();

  const { data: preferenceData, isLoading } = usePreference();

  useEffect(() => {
    function onStorage(event: StorageEvent) {
      // zustand persist stores under the 'app' key
      if (event.key === 'app' && event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue);
          if (parsed?.state?.preferenceDataUpdatedAt) {
            queryClient.invalidateQueries({ queryKey: ['preference'] });
          }
        } catch {
          // ignore parse errors
        }
      }
    }

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [queryClient]);

  if (isLoading) return <Loader isOpen={true} />;

  return (
    <PreferenceContext.Provider value={{ preferenceData: preferenceData?.data ?? null }}>
      {children}
    </PreferenceContext.Provider>
  );
};

export const usePreferenceContext = (): PreferenceContextType => {
  const context = useContext(PreferenceContext);
  if (context === undefined) {
    throw new Error('usePreferenceContext must be used within PreferenceProvider');
  }
  return context;
};
